import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import { FiberManualRecord } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../utilities/axios';

export default function CollaborationInvitation() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [organization, setOrganization] = useState(null);
  const [invitedEmail, setInvitedEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const token = searchParams.get('token');
  const navigate = useNavigate(); 

  useEffect(() => {
    const validateToken = async () => {
      try {
        const { data } = await axiosInstance.get('/api/v2/organizations/invite/validate', {
          params: { token }
        });

        if (data.success) {
          setOrganization(data.data.organization);
          const tokenEmail = data?.data?.tokenData?.email || '';
          setInvitedEmail(tokenEmail);
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          const recoveryEmail = (user?.recoveryEmail || '').toLowerCase();
          const invited = (tokenEmail || '').toLowerCase();
          if (recoveryEmail && invited && recoveryEmail !== invited) {
            setError("The invited email does not match your account's email");
            return;
          }
        } else {
          setError(data.message || 'Invalid invitation');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to validate invitation');
      } finally {
        setLoading(false);
      }
    };

    if (token) validateToken();
    else setError('Missing invitation token');
  }, [token]);

  const handleAccept = async () => {
    try {
      setLoading(true);
      await axiosInstance.post('/api/v2/organizations/invite/accept', null, {
        params: { token }
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/listOrganization'); 
      }, 2000);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
        <Alert severity="success">
          Successfully joined {organization?.name}!
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Card elevation={0} sx={{ 
        p: 4, 
        maxWidth: 600, 
        width: '100%', 
        border: 'none', 
        boxShadow: 'none',
        backgroundColor: 'transparent' 
      }}>
        <div style={{ textAlign: 'center', marginBottom: '25px' }}>
          <img 
            src="https://cdn.clikkle.com/images/projects/logo/2025/Clikkle%20Project%20Blue%20(%20Black%20Text).png" 
            alt="logo" 
            style={{ width: '300px', margin: '0 auto' }}
          />
        </div>

        <Typography variant="h5" component="div" gutterBottom textAlign="center">
          clikkle invited you to collaborate on
        </Typography>
        <Typography variant="h4" component="div" gutterBottom textAlign="center" sx={{ fontWeight: 600 }}>
          {organization?.name || 'Loading...'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mt: 3, mb: 4 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            sx={{ py: 1.5 }}
            onClick={handleAccept}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Accept invitation'}
          </Button>

          <Button
            variant="contained"
            fullWidth
            size="large"
            sx={{ 
              py: 1.5, 
              backgroundColor: '#d32f2f', 
              '&:hover': { backgroundColor: '#7e0000' } 
            }}
            onClick={() => {
              window.location.href = "https://clikkle.com";
            }}
          >
            Decline Invitation
          </Button>
        </Box>

        <Typography variant="subtitle1" gutterBottom>
          You will be able to work in {organization?.name} and Organization Owner will be able to see:
        </Typography>

        <List dense sx={{ py: 0 }}>
          {[
            'Your public profile information',
            'Certain activity within this project',
            'Country of request origin',
            'Is this user sending spam or malicious content?'
          ].map((item) => (
            <ListItem key={item} sx={{ py: 0 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <FiberManualRecord fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={item} />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 4 }} />
        <Typography variant="caption" component="div" color="text.secondary" textAlign="center" sx={{ mt: 4 }}>
          © Clikkle
        </Typography>
      </Card>
    </Box>
  );
}